import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState, ReactNode, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { enqueueSnackbar } from 'notistack';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
  UseFormTrigger,
  UseFormClearErrors
} from 'react-hook-form';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { RootState } from '@/src/datastore/store';
import { IAttributeOptions, ILooseObject, IPagination } from '@/src/types/common.interface';
import AppConfig from '@/src/config/AppConfig';
import { IAttributeDef } from '@/src/config/AttributesConfig';
import AllRolesService from '@/src/services/all-roles/allRolesService';
import AllApiConfigService from '@/src/services/api-config/allApiConfigService';
import AllUsersService from '@/src/services/all-users/allUsersService';
import TestCaseAccordion, {
  SingleTestCaseForm,
  TestCaseFormValues
} from '@/src/components/mvel-editor/TestCaseAccordion';

interface MVELTestCasesProps {
  control: Control<TestCaseFormValues>;
  errors: FieldErrors<TestCaseFormValues>;
  watch: UseFormWatch<TestCaseFormValues>;
  trigger: UseFormTrigger<TestCaseFormValues>;
  handleRunTestCase: (index: number) => void;
  clearErrors: UseFormClearErrors<TestCaseFormValues>;
  applicationKey?: string;
  loadingTestCases: Record<string, boolean>;
  testCaseResults: Record<string, any>;
}

const MVELTestCases = (props: MVELTestCasesProps) => {
  const {
    control,
    errors,
    watch,
    trigger,
    handleRunTestCase,
    clearErrors,
    applicationKey,
    loadingTestCases,
    testCaseResults
  } = props;

  const [currentTestCaseCount, setCurrentTestCaseCount] = useState<number>(0);
  const [extraConfig, setExtraConfig] = useState<{
    rolesOptions: IAttributeOptions[];
    apiConfigOptions: IAttributeOptions[];
    organizationOptions: IAttributeOptions[];
    userTypeOptions: IAttributeOptions[];
    userAttributesDefinition: IAttributeDef[];
  }>({
    rolesOptions: [],
    apiConfigOptions: [],
    organizationOptions: [],
    userTypeOptions: [],
    userAttributesDefinition: []
  });

  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const paginationData: IPagination = useMemo(
    () => ({
      page: 0,
      size: -1
    }),
    []
  );

  const { append: appendTestCase, remove: removeTestCase } = useFieldArray({
    control,
    name: 'testCases'
  });

  const createDefaultTestCase = (testcaseName: string): SingleTestCaseForm => ({
    id: uuidv4(),
    test_case_name: testcaseName,
    api_config: '',
    params: [{ key: '', value: '' }],
    headers: [{ key: '', value: '' }],
    body: '',
    organization: '',
    user_name: '',
    user_type: '',
    user_attributes: [],
    role_names: []
  });

  const handleRunSingleTest = async (index: number, id: string) => {
    const isValid = await trigger(`testCases.${index}`);
    if (!isValid) return;
    handleRunTestCase(index);
  };

  const handleRunAllTestCases = async () => {
    const testCases = watch().testCases;
    if (!testCases || testCases.length === 0) return;

    for (let i = 0; i < testCases.length; i++) {
      const isValid = await trigger(`testCases.${i}`);
      if (isValid) {
        handleRunTestCase(i);
      }
    }
  };

  const handleCloneTestCase = (id: string) => {
    const allTestCases = watch().testCases;
    const testCaseToClone = allTestCases.find((tc) => tc.id === id);

    if (!testCaseToClone) return;

    const newCopiedTestCase: SingleTestCaseForm = JSON.parse(JSON.stringify(testCaseToClone));
    newCopiedTestCase.id = uuidv4();

    if (newCopiedTestCase.user_attributes && Array.isArray(newCopiedTestCase.user_attributes)) {
      newCopiedTestCase.user_attributes = newCopiedTestCase.user_attributes.map((attr) => ({
        ...attr,
        id: uuidv4()
      }));
    }

    const originalName = testCaseToClone.test_case_name || '';
    const nameMatch = originalName.match(/^(.*?)(\d+)$/);

    let newName = `${originalName} (Copy)`;

    if (nameMatch) {
      const prefix = nameMatch[1];
      const maxNumber = allTestCases.reduce((max, tc) => {
        const tcName = tc.test_case_name || '';
        if (tcName.startsWith(prefix)) {
          const m = tcName.match(/^(.*?)(\d+)$/);
          if (m && m[1] === prefix) {
            const num = parseInt(m[2], 10);
            return num > max ? num : max;
          }
        }
        return max;
      }, 0);

      newName = `${prefix}${maxNumber + 1}`;
    }

    newCopiedTestCase.test_case_name = newName;
    appendTestCase(newCopiedTestCase);
  };

  const handleAddNewTestCase = () => {
    appendTestCase(createDefaultTestCase(`Test case ${currentTestCaseCount + 1}`));
    setCurrentTestCaseCount((pre) => pre + 1);
  };

  const clearErrorsAny = useCallback(
    (name: string) => {
      clearErrors(name as any);
    },
    [clearErrors]
  );

  const fetchRoles = useCallback(
    async (appKey: string) => {
      const results = await AllRolesService.getAllRolesList({
        pagination: paginationData,
        filters: {
          application_keys: appKey
        }
      });
      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        return [];
      }
      if (!results.error.code) {
        return results.rows.map((role: any) => ({
          value: JSON.stringify(role),
          label: role.ui_label ?? '-',
          details: role?.description ?? '-'
        })) as IAttributeOptions[];
      }
      return [];
    },
    [paginationData]
  );

  const fetchApiConfigs = useCallback(async (appKey: string) => {
    const results = await AllApiConfigService.getApiConfigList({
      applicationKey: appKey,
      actions: {
        dispatchConfigRowData: () => {}
      }
    });

    if (results.error.message) {
      enqueueSnackbar(results.error.message, {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
      return [];
    }
    if (!results.error.code) {
      return (results.rows ?? []).map((row: any) => {
        const renderDetails = (): ReactNode => {
          return (
            <Box color="GrayText">
              <Box display="flex">
                <Typography fontWeight="bold" variant="caption">
                  URI:{' '}
                </Typography>
                <Typography variant="caption"> {row?.uri}</Typography>
              </Box>
              <Box display="flex">
                <Typography fontWeight="bold" variant="caption">
                  Method:{' '}
                </Typography>
                <Typography variant="caption"> {row?.method}</Typography>
                {Object.keys(row?.policy || {})?.length > 0 && (
                  <>
                    <Typography fontWeight="bold" variant="caption">
                      {' '}
                      | Policy:{' '}
                    </Typography>
                    <Typography variant="caption"> {row?.policy}</Typography>
                  </>
                )}
              </Box>
            </Box>
          );
        };
        return {
          label:
            row?.api_config_name ?? row?.name ?? row?.api_config_key ?? row?.id ?? 'API Config',
          value: JSON.stringify(row) ?? '',
          details: renderDetails()
        };
      }) as IAttributeOptions[];
    }
    return [];
  }, []);

  useEffect(() => {
    const organizationOptions: IAttributeOptions[] = userDetails?.organizations?.map(
      (organization: ILooseObject) =>
        ({ value: organization?.name, label: organization?.ui_label }) as IAttributeOptions
    );
    const userTypeOptions: IAttributeOptions[] =
      typeof userDetails?.user_type === 'string'
        ? [{ label: userDetails.user_type, value: userDetails.user_type }]
        : [];

    setExtraConfig((prev) => ({
      ...prev,
      organizationOptions,
      userTypeOptions
    }));
  }, [userDetails]);

  useEffect(() => {
    if (!applicationKey) return;

    fetchRoles(applicationKey).then((rolesOptions) => {
      setExtraConfig((prev) => ({
        ...prev,
        rolesOptions
      }));
    });

    fetchApiConfigs(applicationKey).then((apiConfigOptions) => {
      setExtraConfig((prev) => ({
        ...prev,
        apiConfigOptions
      }));
    });
  }, [applicationKey, fetchRoles, fetchApiConfigs]);

  useEffect(() => {
    AllUsersService.getUserAttributesDefinition()
      .then((results) => {
        if (results.error?.message) {
          enqueueSnackbar(results.error.message, {
            variant: 'error',
            autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
          });
          return;
        }
        if (!results.error?.code) {
          const defs = Array.isArray(results?.data) ? (results.data as IAttributeDef[]) : [];
          setExtraConfig((prev) => ({
            ...prev,
            userAttributesDefinition: defs
          }));
        }
      })
      .catch((error) =>
        enqueueSnackbar(error?.toString(), {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        })
      );
  }, []);

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Test cases</Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<PlayArrowIcon />}
            variant="contained"
            color="success"
            size="small"
            sx={{ textTransform: 'none', backgroundColor: '#2e7d32' }}
            disabled={!applicationKey || watch().testCases.length === 0}
            onClick={handleRunAllTestCases}
          >
            Run all test cases
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            size="small"
            sx={{ textTransform: 'none' }}
            disabled={!applicationKey}
            onClick={handleAddNewTestCase}
          >
            Add new test case
          </Button>
        </Box>
      </Box>
      {watch().testCases.map((field, index) => (
        <Box key={field.id} mb={2}>
          <TestCaseAccordion
            control={control}
            errors={errors}
            testCaseIndex={index}
            fieldId={field.id}
            handleCloneTestCase={() => handleCloneTestCase(field?.id)}
            onRemoveTestCase={() => removeTestCase(index)}
            organizationOptions={extraConfig.organizationOptions}
            userTypeOptions={extraConfig.userTypeOptions}
            rolesOptions={extraConfig.rolesOptions}
            apiConfigOptions={extraConfig.apiConfigOptions}
            userAttributesDefinition={extraConfig.userAttributesDefinition}
            clearErrors={clearErrorsAny}
            onRunTest={() => handleRunSingleTest(index, field?.id)}
            isLoading={loadingTestCases[field.id]}
          />
          {testCaseResults[field.id] && (
            <Box
              mt={1}
              p={1.5}
              borderRadius={1}
              sx={{
                backgroundColor: testCaseResults[field.id].error ? '#fff5f5' : '#f6fff6',
                border: '1px solid',
                borderColor: testCaseResults[field.id].error ? '#ffa2a2' : '#a2ffa2',
                maxHeight: '300px',
                overflow: 'auto'
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                color={testCaseResults[field.id].error ? 'error' : 'success.main'}
                display="block"
                mb={0.5}
              >
                {testCaseResults[field.id].error ? 'Error Message:' : 'Success Response:'}
              </Typography>
              <Box
                component="pre"
                sx={{ m: 0, fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
              >
                {testCaseResults[field.id].error
                  ? testCaseResults[field.id].error
                  : JSON.stringify(testCaseResults[field.id].success, null, 2)}
              </Box>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default MVELTestCases;
